import { z } from 'zod';
import { addHourlyPremiumIssues } from './schemaHelpers';

const parseTime = (time: string | null): number | null => {
  if (!time) return null;
  const parts = time.split(':').map(Number);
  if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
  return parts[0] * 60 + parts[1];
};

const getOvernightDurationInHours = (start: string | null, end: string | null): number | null => {
  const startMinutes = parseTime(start);
  const endMinutes = parseTime(end);

  if (startMinutes === null || endMinutes === null) return null;

  if (endMinutes >= startMinutes) {
    return (endMinutes - startMinutes) / 60;
  } else {
    // Crosses midnight
    return (24 * 60 - startMinutes + endMinutes) / 60;
  }
};

const overnightConfigSchema = z.object({
  applyOvernightPremium: z.boolean().nullable().optional(),
  startTime: z.string().nullable().optional(),
  endTime: z.string().nullable().optional(),
  overnightMethod: z.string().nullable().optional(),
  premiumType: z.string().nullable().optional(),
  premiumValue: z.number().nullable().optional(),
  flatRateAmount: z.number().nullable().optional(),
  timeCreditMinutes: z.number().nullable().optional(),
  thresholdEnabled: z.boolean().nullable().optional(),
  thresholdType: z.string().nullable().optional(),
  percentageThreshold: z.number().nullable().optional(),
  hoursThreshold: z.number().nullable().optional(),
  // Unified rate-offset fields (Phase 9).
  floorSource: z.enum(['none', 'fixed_tier']).nullable().optional(),
  overtimeOffset: z.number().int().min(0).max(10).nullable().optional(),
  floorTierCode: z.string().nullable().optional(),
  floorTierCustomValue: z.number().min(1).max(9.99).nullable().optional(),
}).passthrough();

export const overnightSchema = z
  .object({
    overnightConfig: overnightConfigSchema.nullable().optional(),
  })
  .passthrough()
  .superRefine((data, ctx) => {
    const config = data.overnightConfig;

    if (!config?.applyOvernightPremium) return;

    if (!config.startTime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['overnightConfig.startTime'],
        message: 'Start time is required',
      });
    }
    if (!config.endTime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['overnightConfig.endTime'],
        message: 'End time is required',
      });
    }
    if (config.startTime && config.endTime && config.startTime === config.endTime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['overnightConfig.endTime'],
        message: 'End time cannot be the same as start time',
      });
    }
    if (!config.overnightMethod) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['overnightConfig.overnightMethod'],
        message: 'Overnight Premium method is required',
      });
    }

    if (config.overnightMethod === 'hourly_premium') {
      addHourlyPremiumIssues(ctx, config.premiumType, config.premiumValue, 'overnightConfig.premiumType', 'overnightConfig.premiumValue');
    }

    if (config.overnightMethod === 'flat_rate' && (!config.flatRateAmount || config.flatRateAmount <= 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['overnightConfig.flatRateAmount'],
        message: 'Flat rate amount must be greater than 0',
      });
    }

    if (config.overnightMethod === 'credit_extra_time' && (!config.timeCreditMinutes || config.timeCreditMinutes <= 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['overnightConfig.timeCreditMinutes'],
        message: 'Credit minutes must be greater than 0',
      });
    }

    if (config.thresholdEnabled) {
      if (!config.thresholdType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['overnightConfig.thresholdType'],
          message: 'Threshold type is required when enabled.',
        });
      } else if (config.thresholdType === 'percentage') {
        if (!config.percentageThreshold || config.percentageThreshold <= 0 || config.percentageThreshold > 100) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['overnightConfig.percentageThreshold'],
            message: 'Percentage must be between 1 and 100',
          });
        }
      } else if (config.thresholdType === 'hours') {
        const overnightDuration = getOvernightDurationInHours(config.startTime ?? null, config.endTime ?? null);
        if (!config.hoursThreshold || config.hoursThreshold <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['overnightConfig.hoursThreshold'],
            message: 'Hours must be greater than 0',
          });
        } else if (overnightDuration !== null && config.hoursThreshold > overnightDuration) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['overnightConfig.hoursThreshold'],
            message: `Hours cannot exceed window duration of ${overnightDuration.toFixed(2).replace(/\.00$/, '')} hrs`,
          });
        }
      }
    }
  });

export type OvernightSchemaData = z.infer<typeof overnightSchema>;
